import { DatePicker, Form, Input, Select, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import moment, { type Moment } from 'moment';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import startCase from 'lodash/startCase';
import CloseIcon from 'assets/svg/CloseIcon';
import type {
    FormFieldConfig,
    DimSelectFieldConfig,
    SelectFieldConfig,
    YearPickerFieldConfig,
} from '../config/tpoGenConfig';
import type { FieldUiState } from '../rules/types';
import type { RuleOption } from '../config/fieldRulesConfig';
import { toMoment } from '../utils/dateCoerce';
import { tpoDimFieldName, tpoFieldTestId, tpoOptionTestId, tpoStateAttrs, type TpoScope } from '../config/testIds';
import { useEditSession } from '../PlanDetail/EditSession/EditSessionContext';
import { DimSelect } from './DimSelect';
import type { UseDimOptionsHook } from './DimSelect';

export type { UseDimOptionsHook, UseDimOptionsParams, UseDimOptionsResult, DimSelectProps } from './DimSelect';
export { DimSelect } from './DimSelect';

const YearPicker = generatePicker<Dayjs>(dayjsGenerateConfig);

/**
 * Wraps one field. The test id goes here rather than on the control because
 * `Form.Item` clones its child and can drop unknown props, and because the
 * rule-engine state attributes need a node that exists for every field type.
 */
const FieldShell = ({
    children,
    className = 'w-[26rem]',
    testId,
    state,
}: {
    children: ReactNode;
    className?: string;
    testId?: string;
    state?: FieldUiState;
}) => (
    <div className={className} data-testid={testId} {...tpoStateAttrs(state)}>
        {children}
    </div>
);

/**
 * Adds a per-option test id derived from the option `value`, never the label.
 *
 * An option whose value has no addressable id (`tpoOptionTestId` declines to
 * build one for a purely symbolic value) carries `undefined`, which React drops,
 * so that option renders with no `data-testid` at all. The option itself stays
 * in the list — it is a real choice the user can pick — and giving it a fallback
 * id would only recreate the collision the registry refuses to emit.
 */
const withOptionTestIds = <T extends { value?: string | number }>(
    options: T[] | undefined,
    baseTestId: string,
): (T & { 'data-testid': string | undefined })[] | undefined =>
    options?.map((option) => ({ ...option, 'data-testid': tpoOptionTestId(baseTestId, option.value ?? '') }));

export const TagComponent = ({
    name,
    onRemove,
    testId,
}: {
    name: string;
    onRemove?: () => void;
    testId?: string;
}) => {
    return (
        <div
            className="flex flex-row w-[5.75rem] bg-[#F4F4F5] rounded-md p-2 items-center justify-between"
            data-testid={testId}
        >
            <Tooltip title={name} showArrow={false}>
                <p className="text-xs truncate font-medium">{name}</p>
            </Tooltip>
            <span
                className="cursor-pointer"
                data-testid={testId ? `${testId}_remove` : undefined}
                onClick={onRemove}
            >
                <CloseIcon size={8} />
            </span>
        </div>
    );
};

export const TagFieldRow = ({
    field,
    values,
    onRemoveTag,
    parentValue,
    useDimOptions,
    testId,
}: {
    field: DimSelectFieldConfig;
    values: string[];
    onRemoveTag: (fieldName: string, value: string) => void;
    parentValue?: string;
    useDimOptions: UseDimOptionsHook;
    testId?: string;
}) => {
    const fieldName = tpoDimFieldName(field.dimension, field.attributeType);

    const { options } = useDimOptions({
        dimension: field.dimension,
        attributeType: field.attributeType,
        lowestAttribute: field.lowestAttribute,
        filters: {},
        selectedDimension: field.dependsOn
            ? {
                  value: parentValue ?? '',
                  dimension: field.dependsOn.dimension,
                  attributeType: field.dependsOn.attributeType,
              }
            : { value: '', dimension: field.dimension, attributeType: field.attributeType },
        searchString: '',
    });

    const valueLabelMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const opt of options) {
            map.set(String(opt.value), opt.label);
        }
        return map;
    }, [options]);

    return (
        <div className="flex flex-col gap-y-2" data-testid={testId}>
            <p className="text-xs text-[#52525C]">{`${startCase(field.attributeType)}s Selected`}</p>
            <div className="flex flex-row flex-wrap gap-2">
                {values.map((val) => (
                    <TagComponent
                        key={val}
                        name={valueLabelMap.get(String(val)) ?? String(val)}
                        testId={testId ? tpoOptionTestId(testId, val) : undefined}
                        onRemove={() => onRemoveTag(fieldName, val)}
                    />
                ))}
            </div>
        </div>
    );
};

const YEAR_DISABLED_DATE_FNS: Record<string, (date: Dayjs) => boolean> = {
    currentAndNext: (date) => !(date.year() === dayjs().year() || date.year() === dayjs().year() + 1),
};

/**
 * Builds `disabledDate` / `defaultPickerValue` for a config-driven datepicker from
 * the rule-engine-derived `dateConstraints`. Mirrors promo `renderPromoField` using
 * `moment` because antd's default `DatePicker` is Moment-typed. Plan-year minimum
 * applies when `minFromContext === 'planYearStart'` and `planYear` is known.
 */
const buildDatePickerConstraints = (dateConstraints?: FieldUiState['dateConstraints'], planYear?: number) => {
    const minDate = toMoment(dateConstraints?.minDate);
    const maxDate = toMoment(dateConstraints?.maxDate);
    const disablePast = dateConstraints?.disablePast;
    const planYearMinDate =
        dateConstraints?.minFromContext === 'planYearStart' && typeof planYear === 'number'
            ? moment().year(planYear).startOf('year')
            : undefined;

    const hasConstraints = !!(minDate || maxDate || disablePast || planYearMinDate);
    const disabledDate = hasConstraints
        ? (current: Moment) => {
              if (!current) return false;
              if (planYearMinDate && current.isBefore(planYearMinDate, 'day')) return true;
              if (disablePast && current.isBefore(moment(), 'day')) return true;
              if (minDate && current.isBefore(minDate, 'day')) return true;
              if (maxDate && current.isAfter(maxDate, 'day')) return true;
              return false;
          }
        : undefined;

    const defaultPickerCandidates = [
        planYearMinDate,
        disablePast ? moment().startOf('day') : undefined,
        minDate,
    ].filter((value): value is Moment => !!value);
    const defaultPickerValue =
        defaultPickerCandidates.length > 0
            ? defaultPickerCandidates.reduce((latestDate, candidateDate) =>
                  candidateDate.isAfter(latestDate, 'day') ? candidateDate : latestDate,
              )
            : undefined;

    return { disabledDate, defaultPickerValue };
};

const SelectFieldRenderer = ({
    field,
    fieldClassName,
    disabled,
    options,
    testId,
    state,
}: {
    field: SelectFieldConfig;
    fieldClassName?: string;
    disabled?: boolean;
    options?: RuleOption[];
    testId: string;
    state?: FieldUiState;
}) => {
    const resolvedOptions = options ?? field.options;
    const optionsWithTestIds = useMemo(() => withOptionTestIds(resolvedOptions, testId), [resolvedOptions, testId]);

    return (
        <FieldShell className={fieldClassName} testId={testId} state={state}>
            <Form.Item
                label={field.label}
                name={field.name}
                rules={
                    field.required && !disabled ? [{ required: true, message: `${field.label} is required` }] : undefined
                }
            >
                <Select
                    placeholder={field.placeholder}
                    options={optionsWithTestIds}
                    disabled={disabled}
                    data-testid={`${testId}_select`}
                />
            </Form.Item>
        </FieldShell>
    );
};

const YearPickerFieldRenderer = ({
    field,
    fieldClassName,
    testId,
    state,
}: {
    field: YearPickerFieldConfig;
    fieldClassName?: string;
    testId: string;
    state?: FieldUiState;
}) => {
    const disabledDate = field.allowedYears ? YEAR_DISABLED_DATE_FNS[field.allowedYears] : undefined;
    return (
        <FieldShell className={fieldClassName} testId={testId} state={state}>
            <Form.Item
                label={field.label}
                name={field.name}
                rules={field.required ? [{ required: true, message: `${field.label} is required` }] : undefined}
            >
                <YearPicker
                    placeholder={field.placeholder}
                    allowClear={false}
                    style={{ width: '100%', borderRadius: 6 }}
                    disabledDate={disabledDate}
                    picker="year"
                    data-testid={`${testId}_input`}
                />
            </Form.Item>
        </FieldShell>
    );
};

export interface ConfigDrivenFormFieldsProps {
    fields: FormFieldConfig[];
    isEditMode?: boolean;
    useDimOptions: UseDimOptionsHook;
    enabled?: boolean;
    fieldClassName?: string;
    /** Per-field UI state from the rule engine. Used to apply disabled state to select fields. */
    derivedStates?: Record<string, FieldUiState>;
    /** Plan year, used to resolve `minFromContext: 'planYearStart'` date constraints. */
    planYear?: number;
    /**
     * Form surface these fields belong to. Required because it is the first
     * segment of every field's test id, so the same config field rendered on two
     * surfaces stays independently addressable.
     */
    scope: TpoScope;
}

const ConfigDrivenFormFields = ({
    fields,
    isEditMode,
    useDimOptions,
    enabled = true,
    fieldClassName = 'w-[26rem]',
    derivedStates,
    planYear,
    scope,
}: ConfigDrivenFormFieldsProps) => {
    const form = Form.useFormInstance();
    const { canEdit } = useEditSession();
    const prevParentKey = useRef('');

    const dimSelectFields = fields.filter((f): f is DimSelectFieldConfig => f.type === 'dimSelect');
    const dependentFields = dimSelectFields.filter((f) => f.dependsOn);
    const tagFields = dimSelectFields.filter((f) => f.showSelectedTags);

    const parentFieldNames = dependentFields.map((f) => f.dependsOn!.fieldName);
    const tagFieldNames = tagFields.map((f) => tpoDimFieldName(f.dimension, f.attributeType));

    const allValues: Record<string, any> = Form.useWatch([], form) ?? {};

    const watchedParents: Record<string, string> = {};
    for (const name of parentFieldNames) {
        watchedParents[name] = allValues[name] ?? '';
    }

    const watchedTagValues: Record<string, string[]> = {};
    for (const name of tagFieldNames) {
        watchedTagValues[name] = allValues[name] ?? [];
    }

    const currentParentKey = JSON.stringify(watchedParents);

    useEffect(() => {
        const prev = prevParentKey.current;

        if (currentParentKey === prev) return;

        const prevValues: Record<string, any> = prev ? JSON.parse(prev) : {};
        const allPrevEmpty = Object.values(prevValues).every(
            (v) => !v || v === '' || (Array.isArray(v) && v.length === 0),
        );

        const currentValues: Record<string, any> = JSON.parse(currentParentKey);
        const allCurrentEmpty = Object.values(currentValues).every(
            (v) => !v || v === '' || (Array.isArray(v) && v.length === 0),
        );

        prevParentKey.current = currentParentKey;

        if (isEditMode && allPrevEmpty) return;

        for (const field of dependentFields) {
            const fieldName = field.name ?? tpoDimFieldName(field.dimension, field.attributeType);
            if (field.isMultiSelect && !allCurrentEmpty) {
                continue;
            }
            form.setFieldsValue({ [fieldName]: field.isMultiSelect ? [] : undefined });
        }
    }, [currentParentKey, form]);

    const handleRemoveTag = (fieldName: string, value: string) => {
        const current: string[] = form.getFieldValue(fieldName) ?? [];
        form.setFieldsValue({ [fieldName]: current.filter((v) => v !== value) });
    };

    const renderField = (field: FormFieldConfig, index: number) => {
        const fieldState = field.name != null ? derivedStates?.[field.name] : undefined;
        // Read-only wins over any rule decision. Outside an EditSessionProvider
        // (e.g. the create-plan modal) `canEdit` defaults true, so surfaces that
        // are not lock-governed are unaffected.
        const fieldDisabled = !canEdit || (fieldState?.disabled ?? false);
        const fieldOptions = fieldState?.options;
        // `dimSelect` fields carry no `name`, so they fall back to the same
        // dimension/attributeType pair the form uses as their value key.
        const resolvedName =
            field.name ??
            (field.type === 'dimSelect' ? tpoDimFieldName(field.dimension, field.attributeType) : String(index));
        const testId = tpoFieldTestId(scope, resolvedName);
        switch (field.type) {
            case 'input': {
                const inputDisabled = fieldDisabled || field.disabled;
                const inputRequired = fieldState?.required || field.required;
                return (
                    <FieldShell key={index} className={fieldClassName} testId={testId} state={fieldState}>
                        <Form.Item
                            label={field.label}
                            name={field.name}
                            rules={
                                inputRequired && !inputDisabled
                                    ? [{ required: true, message: `${field.label} is required` }]
                                    : undefined
                            }
                        >
                            <Input placeholder={field.placeholder} disabled={inputDisabled} data-testid={`${testId}_input`} />
                        </Form.Item>
                    </FieldShell>
                );
            }
            case 'datepicker': {
                const dateDisabled = fieldDisabled || field.disabled;
                const dateRequired = fieldState?.required || field.required;
                const { disabledDate, defaultPickerValue } = buildDatePickerConstraints(
                    fieldState?.dateConstraints,
                    planYear,
                );
                return (
                    <FieldShell key={index} className={fieldClassName} testId={testId} state={fieldState}>
                        <Form.Item
                            label={field.label}
                            name={field.name}
                            rules={
                                dateRequired && !dateDisabled
                                    ? [{ required: true, message: `${field.label} is required` }]
                                    : undefined
                            }
                        >
                            <DatePicker
                                disabled={dateDisabled}
                                placeholder={field.placeholder}
                                allowClear={false}
                                disabledDate={disabledDate}
                                defaultPickerValue={defaultPickerValue}
                                style={{ width: '100%', borderRadius: 6 }}
                                picker="date"
                                data-testid={`${testId}_input`}
                            />
                        </Form.Item>
                    </FieldShell>
                );
            }
            case 'select':
                return (
                    <SelectFieldRenderer
                        key={index}
                        field={field}
                        fieldClassName={fieldClassName}
                        disabled={fieldDisabled}
                        options={fieldOptions}
                        testId={testId}
                        state={fieldState}
                    />
                );
            case 'yearPicker':
                return (
                    <YearPickerFieldRenderer
                        key={index}
                        field={field}
                        fieldClassName={fieldClassName}
                        testId={testId}
                        state={fieldState}
                    />
                );
            case 'dimSelect': {
                const resolvedLabel = field.label ?? startCase(field.attributeType);
                const parentValue = field.dependsOn ? watchedParents[field.dependsOn.fieldName] : undefined;

                return (
                    <FieldShell key={index} className={fieldClassName} testId={testId} state={fieldState}>
                        <Form.Item
                            label={resolvedLabel}
                            name={resolvedName}
                            rules={
                                field.required
                                    ? [{ required: true, message: `${resolvedLabel} is required` }]
                                    : undefined
                            }
                        >
                            <DimSelect
                                dimension={field.dimension}
                                attributeType={field.attributeType}
                                lowestAttribute={field.lowestAttribute}
                                isMultiSelect={field.isMultiSelect}
                                placeholder={`Select ${resolvedLabel}`}
                                useDimOptions={useDimOptions}
                                enabled={enabled}
                                testId={testId}
                                selectedDimension={
                                    field.dependsOn
                                        ? {
                                              value: parentValue ?? '',
                                              dimension: field.dependsOn.dimension,
                                              attributeType: field.dependsOn.attributeType,
                                          }
                                        : undefined
                                }
                            />
                        </Form.Item>
                    </FieldShell>
                );
            }
        }
    };

    return (
        <>
            <div className="flex flex-row items-start flex-wrap gap-x-6 gap-y-4">{fields.map(renderField)}</div>
            <div className="flex flex-col gap-y-2">
                {tagFields.map((field) => {
                    const fieldName = tpoDimFieldName(field.dimension, field.attributeType);
                    const values = watchedTagValues[fieldName];
                    if (!values || values.length === 0) return null;
                    const parentValue = field.dependsOn ? watchedParents[field.dependsOn.fieldName] : undefined;
                    return (
                        <TagFieldRow
                            key={fieldName}
                            field={field}
                            values={values}
                            onRemoveTag={handleRemoveTag}
                            parentValue={parentValue}
                            useDimOptions={useDimOptions}
                            testId={`${tpoFieldTestId(scope, fieldName)}_tags`}
                        />
                    );
                })}
            </div>
        </>
    );
};

export default ConfigDrivenFormFields;
